import { createContext, createEffect, createSignal, Setter } from 'solid-js';
import { DbSession, Session, isDbSession } from './event-types';

type EventContextType = {
    isEventPage: boolean;
    gotoEventId?: string;
    eventId?: string;
    isDisabledEvent?: boolean;
    eventType?: 'goto-event' | 'in-person' | 'regular';
    isInPerson?: boolean;
    isSessionBased?: boolean;
    sessions?: Session[];
    selectedSessionIndex?: number;
    setSelectedSessionIndex?: (index: number) => void;
    /**
     * Opaque DB session id for the selected session, passed to postForm as sessionId.
     * undefined for non-session events, legacy-fallback sessions (no id), and while
     * availability is still loading; derived via the isDbSession union guard so a
     * LegacySession never appears to carry an id.
     */
    selectedSessionId?: string;
    /**
     * True while the DB-backed availability fetch for a session-based event is still
     * loading. The form submit is gated on this so a sign-up never posts with an
     * undefined sessionId merely because counts have not arrived yet.
     */
    sessionAvailabilityLoading?: boolean;
    /**
     * Refetch DB-backed availability after a failed session reserve, so the selector
     * reflects live counts when a sign-up is rejected because the session just filled / closed /
     * disappeared. undefined for non-session events.
     */
    refetchSessions?: () => void;
    /**
     * Optimistically decrement the selected session spotsLeft after a successful sign-up
     * (cosmetic) so the visitor sees the seat taken before the next availability fetch.
     */
    decrementSelectedSpotsLeft?: () => void;
}

export const EventContext = createContext<EventContextType>({
    isEventPage: false,
    gotoEventId: undefined,
    eventId: undefined,
    isDisabledEvent: undefined,
    eventType: undefined,
    isInPerson: undefined,
    isSessionBased: undefined,
    sessions: undefined,
    selectedSessionIndex: 0,
    setSelectedSessionIndex: undefined,
    selectedSessionId: undefined,
    sessionAvailabilityLoading: false,
    refetchSessions: undefined,
    decrementSelectedSpotsLeft: undefined,
});

type EventContextProviderProps = {
    children: any;
    gotoEventId?: string;
    isDisabledEvent?: boolean;
    eventType?: 'goto-event' | 'in-person' | 'regular';
    isInPerson?: boolean;
    isSessionBased?: boolean;
    sessions?: Session[];
    sessionAvailabilityLoading?: boolean;
    refetchSessions?: () => void;
    mutateSessions?: Setter<DbSession[] | undefined>;
    eventDate?: string;
    eventExpireDate?: string;
    eventTime?: string;
    countryCode?: string;
}

/**
 * Converts date from "March 25, 2026" or "F j, Y" format to "25/03/2026" (DD/MM/YYYY)
 */
const formatDateToDDMMYYYY = (dateStr?: string): string | undefined => {
    if (!dateStr) return undefined;
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return undefined;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
};

/**
 * Trims spaces from time strings: "5:00 pm" → "5:00pm"
 */
const trimTimeSpaces = (time?: string): string => {
    if (!time) return '';
    return time.replace(/\s+/g, '');
};

export const EventContextProvider = (props: EventContextProviderProps) => {
    const [ gotoEventId ] = createSignal(props.gotoEventId);
    const [ isDisabledEvent ] = createSignal(props.isDisabledEvent);
    const [ selectedSessionIndex, setSelectedSessionIndex ] = createSignal(0);
    const [ userPicked, setUserPicked ] = createSignal(false);

    // Default the selection to the first session that is NOT fully booked, once availability
    // resolves (it is fetched client-side after mount, so props.sessions arrives/changes later).
    // A fully-booked session must never be the default — it can't be submitted; if every session
    // is fully booked, nothing is selected (index -1). The visitor's own choice always wins.
    createEffect(() => {
        const list = props.sessions;
        if (!list || userPicked()) {
            return;
        }
        setSelectedSessionIndex(list.findIndex((s) => !(isDbSession(s) && s.isFullyBooked)));
    });

    // Wraps the raw setter so a deliberate visitor selection pins the choice against the
    // default-selection effect above (which would otherwise reset it on the next refetch).
    const selectSession = (index: number) => {
        setUserPicked(true);
        setSelectedSessionIndex(index);
    };

    /**
     * Generates eventId for Eloqua based on event type:
     * - GoTo events: use gotoEventId
     * - In-person events: formatted string with country, dates, and session info
     *
     * Formats:
     * - No sessions, has end-date: {Country}-{StartDate}-to-{EndDate}
     * - No sessions, no end-date: {Country}-{StartDate}
     * - With sessions, has end-date: {Country}-{StartDate}-to-{EndDate}-Session{N}-{startTime}-{endTime}
     * - With sessions, no end-date: {Country}-{StartDate}-Session{N}-{startTime}-{endTime}
     */
    const getEventId = (): string | undefined => {
        // GoTo events use their own ID from WordPress
        // Check gotoEventId first for backward compatibility with legacy events
        // that have gotoEventId set but no eventType
        if (gotoEventId()) {
            return gotoEventId();
        }

        // In-person and regular events get formatted eventId
        const country = props.countryCode || '';
        const startDate = formatDateToDDMMYYYY(props.eventDate);
        const endDate = formatDateToDDMMYYYY(props.eventExpireDate);

        if (!startDate) return undefined;

        let eventId = country ? `${country}-${startDate}` : startDate;

        if (endDate) {
            eventId += `-to-${endDate}`;
        }

        const idx = selectedSessionIndex();
        const session = props.isSessionBased ? props.sessions?.[idx] : undefined;
        if (session) {
            const startTime = trimTimeSpaces(session.startTime);
            const endTime = trimTimeSpaces(session.endTime);
            eventId += `-Session${idx + 1}-${startTime}-${endTime}`;
        } else if (props.eventTime) {
            // Non-session events: include time if available
            eventId += `-${trimTimeSpaces(props.eventTime)}`;
        }

        return eventId;
    };

    // Derive the opaque DB session id from the selected index via the union guard.
    const getSelectedSessionId = (): string | undefined => {
        const s = props.sessions?.[selectedSessionIndex()];
        return s && isDbSession(s) ? s.id : undefined;
    };

    // Optimistic, cosmetic decrement of the chosen session spotsLeft after a successful
    // sign-up. Mutates the DB-sessions resource (via the parent) so the selector reflects the
    // taken seat immediately; the next refetch reconciles with the authoritative server count.
    const decrementSelectedSpotsLeft = () => {
        const idx = selectedSessionIndex();
        props.mutateSessions?.((prev) => {
            if (!prev) {
                return prev;
            }
            return prev.map((session, i) => {
                if (i !== idx || !isDbSession(session) || session.spotsLeft == null) {
                    return session;
                }
                return { ...session, spotsLeft: Math.max(0, session.spotsLeft - 1) };
            });
        });
    };

    return (
        <EventContext.Provider value={{
            isEventPage: true,
            gotoEventId: gotoEventId(),
            get eventId() { return getEventId(); },
            isDisabledEvent: isDisabledEvent(),
            get eventType() { return props.eventType; },
            get isInPerson() { return props.isInPerson; },
            get isSessionBased() { return props.isSessionBased; },
            get sessions() { return props.sessions; },
            get sessionAvailabilityLoading() { return props.sessionAvailabilityLoading; },
            get selectedSessionIndex() { return selectedSessionIndex(); },
            get selectedSessionId() { return getSelectedSessionId(); },
            setSelectedSessionIndex: selectSession,
            refetchSessions: props.refetchSessions,
            decrementSelectedSpotsLeft,
        }}>
            {props.children}
        </EventContext.Provider>
    );
};
